import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';

const PROGRAM_COLLECTION = 'conference_program';
const THEME_COLLECTION = 'conference_theme';
const VENUE_COLLECTION = 'conference_venue';
const SPEAKERS_COLLECTION = 'speakers';
const LEADERSHIP_COLLECTION = 'leadership';
const TOURISM_COLLECTION = 'tourism_activities';
const SUPPORTERS_COLLECTION = 'conference_supporters';

const serializeDoc = (snap) => {
  if (!snap.exists()) return null;
  const d = snap.data();
  return { id: snap.id, ...d };
};

/**
 * Get conference program by day
 */
export const getProgramByDay = async (day) => {
  try {
    const q = query(
      collection(db, PROGRAM_COLLECTION),
      where('day', '==', day),
      orderBy('display_order', 'asc'),
      orderBy('start_time', 'asc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => serializeDoc(d));
  } catch (error) {
    console.error('Error fetching program:', error);
    throw error;
  }
};

/**
 * Get all conference program
 */
export const getAllProgram = async () => {
  try {
    const q = query(
      collection(db, PROGRAM_COLLECTION),
      orderBy('day', 'asc'),
      orderBy('display_order', 'asc'),
      orderBy('start_time', 'asc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => serializeDoc(d));
  } catch (error) {
    console.error('Error fetching program:', error);
    throw error;
  }
};

/**
 * Create program item
 */
export const createProgramItem = async (programData) => {
  try {
    const ref = await addDoc(collection(db, PROGRAM_COLLECTION), programData);
    const snap = await getDoc(ref);
    return serializeDoc(snap);
  } catch (error) {
    console.error('Error creating program item:', error);
    throw error;
  }
};

/**
 * Update program item
 */
export const updateProgramItem = async (id, updates) => {
  try {
    await updateDoc(doc(db, PROGRAM_COLLECTION, id), updates);
    const snap = await getDoc(doc(db, PROGRAM_COLLECTION, id));
    return serializeDoc(snap);
  } catch (error) {
    console.error('Error updating program item:', error);
    throw error;
  }
};

/**
 * Delete program item
 */
export const deleteProgramItem = async (id) => {
  try {
    await deleteDoc(doc(db, PROGRAM_COLLECTION, id));
    return true;
  } catch (error) {
    console.error('Error deleting program item:', error);
    throw error;
  }
};

/**
 * Get conference theme
 */
export const getConferenceTheme = async () => {
  try {
    const q = query(
      collection(db, THEME_COLLECTION),
      orderBy('created_at', 'desc'),
      limit(1)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    return serializeDoc(snapshot.docs[0]);
  } catch (error) {
    console.error('Error fetching theme:', error);
    throw error;
  }
};

/**
 * Upsert conference theme
 */
export const upsertConferenceTheme = async (themeData) => {
  try {
    const col = collection(db, THEME_COLLECTION);
    const snap = await getDocs(col);
    const batch = writeBatch(db);
    snap.docs.forEach((d) => batch.delete(d.ref));
    const ref = doc(col);
    batch.set(ref, themeData);
    await batch.commit();
    const updated = await getDoc(ref);
    return serializeDoc(updated);
  } catch (error) {
    console.error('Error upserting theme:', error);
    throw error;
  }
};

/**
 * Get conference venue
 */
export const getConferenceVenue = async () => {
  try {
    const q = query(
      collection(db, VENUE_COLLECTION),
      orderBy('created_at', 'desc'),
      limit(1)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    return serializeDoc(snapshot.docs[0]);
  } catch (error) {
    console.error('Error fetching venue:', error);
    throw error;
  }
};

/**
 * Upsert conference venue
 */
export const upsertConferenceVenue = async (venueData) => {
  try {
    const col = collection(db, VENUE_COLLECTION);
    const snap = await getDocs(col);
    const batch = writeBatch(db);
    snap.docs.forEach((d) => batch.delete(d.ref));
    const ref = doc(col);
    batch.set(ref, venueData);
    await batch.commit();
    const updated = await getDoc(ref);
    return serializeDoc(updated);
  } catch (error) {
    console.error('Error upserting venue:', error);
    throw error;
  }
};

/**
 * Get all speakers
 */
export const getAllSpeakers = async (featuredOnly = false) => {
  try {
    const col = collection(db, SPEAKERS_COLLECTION);
    const q = featuredOnly
      ? query(col, where('featured', '==', true), orderBy('display_order', 'asc'), orderBy('name', 'asc'))
      : query(col, orderBy('display_order', 'asc'), orderBy('name', 'asc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => serializeDoc(d));
  } catch (error) {
    console.error('Error fetching speakers:', error);
    throw error;
  }
};

/**
 * Create speaker
 */
export const createSpeaker = async (speakerData) => {
  try {
    const ref = await addDoc(collection(db, SPEAKERS_COLLECTION), speakerData);
    const snap = await getDoc(ref);
    return serializeDoc(snap);
  } catch (error) {
    console.error('Error creating speaker:', error);
    throw error;
  }
};

/**
 * Update speaker
 */
export const updateSpeaker = async (id, updates) => {
  try {
    await updateDoc(doc(db, SPEAKERS_COLLECTION, id), updates);
    const snap = await getDoc(doc(db, SPEAKERS_COLLECTION, id));
    return serializeDoc(snap);
  } catch (error) {
    console.error('Error updating speaker:', error);
    throw error;
  }
};

/**
 * Delete speaker
 */
export const deleteSpeaker = async (id) => {
  try {
    await deleteDoc(doc(db, SPEAKERS_COLLECTION, id));
    return true;
  } catch (error) {
    console.error('Error deleting speaker:', error);
    throw error;
  }
};


/**
 * Get all leadership members
 */
export const getAllLeadership = async ({ committee } = {}) => {
  try {
    const col = collection(db, LEADERSHIP_COLLECTION);
    const q = committee
      ? query(col, where('committee', '==', committee))
      : query(col);
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => serializeDoc(d));
  } catch (error) {
    console.error('Error fetching leadership:', error);
    throw error;
  }
};

export const getExecutiveLeadershipByRole = async (role, excludeId) => {
  const col = collection(db, LEADERSHIP_COLLECTION);
  const q = query(col, where('committee', '==', 'executive'), where('role', '==', role));
  const snapshot = await getDocs(q);
  return snapshot.docs
    .map((d) => serializeDoc(d))
    .filter((m) => m?.id && m.id !== excludeId);
};

export const createLeadership = async (data) => {
  try {
    const ref = await addDoc(collection(db, LEADERSHIP_COLLECTION), data);
    const snap = await getDoc(ref);
    return serializeDoc(snap);
  } catch (error) {
    console.error('Error creating leadership member:', error);
    throw error;
  }
};

export const updateLeadership = async (id, updates) => {
  try {
    await updateDoc(doc(db, LEADERSHIP_COLLECTION, id), updates);
    const snap = await getDoc(doc(db, LEADERSHIP_COLLECTION, id));
    return serializeDoc(snap);
  } catch (error) {
    console.error('Error updating leadership member:', error);
    throw error;
  }
};

export const deleteLeadership = async (id) => {
  try {
    await deleteDoc(doc(db, LEADERSHIP_COLLECTION, id));
    return true;
  } catch (error) {
    console.error('Error deleting leadership member:', error);
    throw error;
  }
};

/**
 * Get all tourism activities
 */
export const getAllTourismActivities = async () => {
  try {
    const q = query(
      collection(db, TOURISM_COLLECTION),
      orderBy('display_order', 'asc'),
      orderBy('title', 'asc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => serializeDoc(d));
  } catch (error) {
    console.error('Error fetching tourism activities:', error);
    throw error;
  }
};

/**
 * Create tourism activity
 */
export const createTourismActivity = async (activityData) => {
  try {
    const ref = await addDoc(collection(db, TOURISM_COLLECTION), activityData);
    const snap = await getDoc(ref);
    return serializeDoc(snap);
  } catch (error) {
    console.error('Error creating tourism activity:', error);
    throw error;
  }
};

/**
 * Update tourism activity
 */
export const updateTourismActivity = async (id, updates) => {
  try {
    await updateDoc(doc(db, TOURISM_COLLECTION, id), updates);
    const snap = await getDoc(doc(db, TOURISM_COLLECTION, id));
    return serializeDoc(snap);
  } catch (error) {
    console.error('Error updating tourism activity:', error);
    throw error;
  }
};

/**
 * Delete tourism activity
 */
export const deleteTourismActivity = async (id) => {
  try {
    await deleteDoc(doc(db, TOURISM_COLLECTION, id));
    return true;
  } catch (error) {
    console.error('Error deleting tourism activity:', error);
    throw error;
  }
};

/**
 * Get all supporters
 */
export const getAllSupporters = async () => {
  try {
    const q = query(
      collection(db, SUPPORTERS_COLLECTION),
      orderBy('display_order', 'asc'),
      orderBy('name', 'asc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => serializeDoc(d));
  } catch (error) {
    console.error('Error fetching supporters:', error);
    throw error;
  }
};

/**
 * Create supporter
 */
export const createSupporter = async (supporterData) => {
  try {
    const ref = await addDoc(collection(db, SUPPORTERS_COLLECTION), supporterData);
    const snap = await getDoc(ref);
    return serializeDoc(snap);
  } catch (error) {
    console.error('Error creating supporter:', error);
    throw error;
  }
};

/**
 * Update supporter
 */
export const updateSupporter = async (id, updates) => {
  try {
    await updateDoc(doc(db, SUPPORTERS_COLLECTION, id), updates);
    const snap = await getDoc(doc(db, SUPPORTERS_COLLECTION, id));
    return serializeDoc(snap);
  } catch (error) {
    console.error('Error updating supporter:', error);
    throw error;
  }
};

/**
 * Delete supporter
 */
export const deleteSupporter = async (id) => {
  try {
    await deleteDoc(doc(db, SUPPORTERS_COLLECTION, id));
    return true;
  } catch (error) {
    console.error('Error deleting supporter:', error);
    throw error;
  }
};
